import { z } from 'zod';
import { sortParam } from './sort.js';
/**
 * Speicher und Dokumente (KON-163, KON-164, siehe docs/speicher.md). Jedes Paket hat Speicherplatz inklusive. Er zählt alle Dateien (Dokumente, Anhänge, Belegarchiv
 * und so weiter) und die Datenbank des Mandanten zusammen. Mehr gibt es gegen Aufpreis in Paketen. Die Preise sind Vorschläge, die der Betreiber hier an einer Stelle pflegt.
 */
const GB = 1024 * 1024 * 1024;
/** Speicherplatz im Paket, in Byte */
export const STORAGE_PLAN_BYTES = { basic: 5 * GB, standard: 25 * GB, premium: 100 * GB };
/** Zusatzpakete mit Monatspreis in Cent, netto zuzüglich Umsatzsteuer */
export const STORAGE_PACKS = [
    { key: 'gb10', label: '10 GB', bytes: 10 * GB, priceCents: 190 },
    { key: 'gb50', label: '50 GB', bytes: 50 * GB, priceCents: 690 },
    { key: 'gb250', label: '250 GB', bytes: 250 * GB, priceCents: 2490 },
];
/** Ab diesem Anteil des Speichers warnt Wirkia (Prozent) */
export const STORAGE_WARN_PERCENT = 80;
export const STORAGE_CRITICAL_PERCENT = 95;
export const STORAGE_CATEGORIES = ['documents', 'inquiry', 'employee', 'product', 'vehicle', 'order', 'social', 'finance', 'other', 'database'];
export const STORAGE_CATEGORY_LABELS = {
    documents: 'Dokumente',
    inquiry: 'Anhänge von Anfragen',
    employee: 'Mitarbeiterdateien',
    product: 'Produktbilder',
    vehicle: 'Fahrzeugfotos',
    order: 'Dateien an Aufträgen',
    social: 'Bilder für Beiträge',
    finance: 'Belegarchiv (Buchhaltung)',
    other: 'Fotos und Logo',
    database: 'Datenbank',
};
/** Kategorien, die einzelne Dateien haben, die man in der Speicherverwaltung sieht und löschen kann */
export const FILE_CATEGORIES = ['documents', 'inquiry', 'employee', 'product', 'vehicle', 'order', 'social', 'finance'];
export const bookAddonInput = z.object({ pack: z.enum(STORAGE_PACKS.map((p) => p.key), 'Bitte ein Paket wählen') }).strict();
export const listStorageFilesQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    category: z.enum(FILE_CATEGORIES).optional(),
    q: z.string().trim().max(100).optional(),
    sort: sortParam(['name', 'size', 'createdAt', 'category'], '-size').optional(),
});
export const deleteStorageFilesInput = z
    .object({
    items: z
        .array(z.object({ category: z.enum(FILE_CATEGORIES), id: z.uuid('Ungültige Datei') }).strict())
        .min(1, 'Bitte mindestens eine Datei wählen')
        .max(100, 'Höchstens 100 Dateien auf einmal'),
})
    .strict();
// ---------------------------------------------------------------------------
// Dokumente
// ---------------------------------------------------------------------------
export const DOCUMENT_CATEGORIES = ['contract', 'offer', 'invoice', 'correspondence', 'photo', 'other'];
export const DOCUMENT_CATEGORY_LABELS = {
    contract: 'Vertrag',
    offer: 'Angebot oder Auftrag',
    invoice: 'Rechnung oder Beleg',
    correspondence: 'Schriftverkehr',
    photo: 'Foto',
    other: 'Sonstiges',
};
/** Größte erlaubte Datei bei den Dokumenten */
export const DOCUMENT_MAX_BYTES = 20 * 1024 * 1024;
/**
 * „Gehört zu“: bei wem das Dokument liegt. Ein Kontakt, ein Debitor, ein Kreditor oder ein Mitarbeiter. Debitor und Kreditor sind Kontakte mit Konto, ihre Dokumente
 * stehen auch beim Kontakt.
 */
export const DOCUMENT_OWNER_KINDS = ['contact', 'debtor', 'creditor', 'employee'];
export const DOCUMENT_OWNER_LABELS = { contact: 'Kontakt', debtor: 'Debitor', creditor: 'Kreditor', employee: 'Mitarbeiter' };
/** „Referenz“: wozu das Dokument gehört. Ein Auftrag, ein Angebot, eine Anfrage oder eine Aufgabe. */
export const DOCUMENT_REFERENCE_KINDS = ['order', 'offer', 'inquiry', 'task', 'asset', 'vehicle', 'financing'];
export const DOCUMENT_REFERENCE_LABELS = { order: 'Auftrag', offer: 'Angebot', inquiry: 'Anfrage', task: 'Aufgabe', asset: 'Inventar', vehicle: 'Fahrzeug', financing: 'Finanzierung' };
const ownerLink = z.object({ kind: z.enum(DOCUMENT_OWNER_KINDS, 'Unbekannte Art'), id: z.uuid('Ungültige Kennung') }).strict();
const referenceLink = z.object({ kind: z.enum(DOCUMENT_REFERENCE_KINDS, 'Unbekannte Art'), id: z.uuid('Ungültige Kennung') }).strict();
/** Beim Hochladen stehen die Angaben als Abfrageparameter: je Verweis Art und Kennung, beides oder nichts */
export const uploadDocumentQuery = z
    .object({
    category: z.enum(DOCUMENT_CATEGORIES).default('other'),
    ownerKind: z.enum(DOCUMENT_OWNER_KINDS).optional(),
    ownerId: z.uuid('Ungültige Kennung').optional(),
    referenceKind: z.enum(DOCUMENT_REFERENCE_KINDS).optional(),
    referenceId: z.uuid('Ungültige Kennung').optional(),
    note: z.string().trim().max(1000).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.ownerKind === undefined) !== (v.ownerId === undefined))
        ctx.addIssue({ code: 'custom', path: ['ownerId'], message: 'Gehört zu braucht Art und Kennung' });
    if ((v.referenceKind === undefined) !== (v.referenceId === undefined))
        ctx.addIssue({ code: 'custom', path: ['referenceId'], message: 'Die Referenz braucht Art und Kennung' });
});
export const updateDocumentInput = z
    .object({
    title: z.string().trim().max(200).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null)),
    category: z.enum(DOCUMENT_CATEGORIES).optional(),
    /** null löst die Zuordnung */
    owner: ownerLink.nullable().optional(),
    reference: referenceLink.nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null)),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listDocumentsQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().max(100).optional(),
    category: z.enum(DOCUMENT_CATEGORIES).optional(),
    /** Dokumente eines Kontakts, auch seiner Konten als Debitor oder Kreditor */
    contactId: z.uuid().optional(),
    employeeId: z.uuid().optional(),
    referenceKind: z.enum(DOCUMENT_REFERENCE_KINDS).optional(),
    referenceId: z.uuid().optional(),
    sort: sortParam(['name', 'category', 'size', 'createdAt'], '-createdAt').optional(),
});
/** Suche nach einem Ziel für „Gehört zu“ (role owner) oder „Referenz“ (role reference) über mehrere Tabellen */
export const listDocumentTargetsQuery = z.object({
    role: z.enum(['owner', 'reference']),
    q: z.string().trim().min(1, 'Bitte einen Suchbegriff eingeben').max(100),
});
/** Größe für die Anzeige: 1,2 MB, 3,4 GB */
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes / 1024;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i += 1;
    }
    return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: value >= 100 ? 0 : 1 }).format(value)} ${units[i]}`;
}
//# sourceMappingURL=storage.js.map